import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Send, Loader2, Megaphone, History } from "lucide-react";

export default function Broadcast() {
  const [text, setText] = useState("");
  const [groupId, setGroupId] = useState("");
  const qc = useQueryClient();

  const groups = useQuery({
    queryKey: ["agent-groups-for-broadcast"],
    queryFn: () => api.get("/agent-groups").then(r => r.data),
  });

  const history = useQuery({
    queryKey: ["broadcast-history"],
    queryFn: () => api.get("/broadcasts").then(r => r.data),
  });

  const send = useMutation({
    mutationFn: () => api.post("/broadcast", { text, group_id: groupId || null }),
    onSuccess: (r) => {
      toast.success(`Delivered to ${r.data.delivered}/${r.data.recipients} · ${r.data.failed} failed`);
      setText("");
      qc.invalidateQueries({ queryKey: ["broadcast-history"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Broadcast failed"),
  });

  return (
    <Layout title="Broadcast." subtitle="Send a message to all agents (or one group) via Telegram">
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-white/10 p-6" data-testid="broadcast-panel">
        <div className="flex items-center gap-3 mb-4">
          <Megaphone className="w-5 h-5 text-[#FF3B30]"/>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">// admin → all telegram-linked agents</div>
            <div className="font-display font-black text-2xl tracking-tight">Speak to the whole floor.</div>
          </div>
        </div>

        <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Target</label>
        <select
          value={groupId}
          onChange={(e) => setGroupId(e.target.value)}
          data-testid="broadcast-target"
          className="w-full bg-[#050505] border border-white/10 px-3 py-2 mb-4 text-[13px]"
        >
          <option value="">All active agents</option>
          {(groups.data || []).map(g => (
            <option key={g.id} value={g.id}>{g.name} ({g.agent_count})</option>
          ))}
        </select>

        <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Message</label>
        <textarea
          rows={8}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type your message. Supports basic HTML like <b>bold</b>. Max 3800 chars."
          data-testid="broadcast-text"
          className="w-full bg-[#050505] border border-white/10 px-3 py-2 mb-2 text-[13px] leading-relaxed"
        />
        <div className="flex items-center justify-between text-[10px] font-mono text-zinc-500 mb-4">
          <span>{text.length}/3800</span>
          <span>agents must have Telegram linked to receive</span>
        </div>

        <button
          onClick={() => {
            if (!text.trim()) { toast.error("Type a message first"); return; }
            if (!window.confirm(groupId
                ? `Broadcast to selected group?`
                : `Broadcast to ALL active agents?`)) return;
            send.mutate();
          }}
          disabled={send.isPending || !text.trim()}
          data-testid="broadcast-send-btn"
          className="w-full bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-medium py-2.5 flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {send.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
          SEND BROADCAST
        </button>
      </div>

      {/* History panel */}
      <div className="border border-white/10 p-5" data-testid="broadcast-history-panel">
        <div className="flex items-center gap-2 mb-3">
          <History className="w-4 h-4 text-zinc-400"/>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">// history</div>
        </div>
        <div className="font-display font-black text-lg tracking-tight mb-3">Recent broadcasts.</div>
        {history.isLoading && <div className="text-[11px] text-zinc-500">Loading…</div>}
        {history.data?.length === 0 && (
          <div className="text-[11px] text-zinc-600 italic">No broadcasts sent yet.</div>
        )}
        <div className="space-y-3 max-h-[520px] overflow-y-auto">
          {(history.data || []).map(b => (
            <div key={b.id} data-testid={`broadcast-hist-${b.id}`} className="border-b border-white/5 pb-2 last:border-0">
              <div className="flex items-center justify-between text-[10px] font-mono text-zinc-500 mb-1">
                <span>{b.sender_name}{b.group_name ? ` → ${b.group_name}` : " → all"}</span>
                <span>{new Date(b.created_at).toLocaleString("en-GB", {hour12: false})}</span>
              </div>
              <div className="text-[12px] text-zinc-300 line-clamp-3 mb-1">{b.text}</div>
              <div className="flex items-center gap-2 text-[10px] font-mono">
                <span className="text-emerald-400">✓ {b.delivered}/{b.recipients}</span>
                {b.failed > 0 && <span className="text-[#FF3B30]">✗ {b.failed}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </Layout>
  );
}
